import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { ReviewCard } from '../components/ReviewCard';
import { ReviewForm } from '../components/ReviewForm';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Star,
  MapPin,
  MessageCircle,
  Instagram,
  ArrowLeft,
  Loader2,
  Phone,
  Calendar,
  CreditCard,
  Lock,
  MessageSquare
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/400x400?text=No+Photo';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

export default function TrainerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trainer, setTrainer] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [canMessage, setCanMessage] = useState(false);

  const fetchTrainerData = useCallback(async () => {
    try {
      const [trainerRes, reviewsRes, pricingRes] = await Promise.all([
        axios.get(`${API_URL}/trainers/${id}`),
        axios.get(`${API_URL}/reviews/${id}`),
        axios.get(`${API_URL}/payments/pricing`)
      ]);
      setTrainer(trainerRes.data);
      setReviews(reviewsRes.data);
      setPricing(pricingRes.data);
      
      // Check if user can message this trainer (has booking)
      if (user) {
        try {
          const msgCheck = await axios.get(`${API_URL}/messages/can-message/${id}`);
          setCanMessage(msgCheck.data.can_message);
        } catch {
          setCanMessage(false);
        }
      }
    } catch (error) {
      console.error('Error fetching trainer:', error);
      toast.error('Failed to load trainer profile');
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    fetchTrainerData();
  }, [fetchTrainerData]);

  // Use trainer's custom session offerings instead of fixed pricing
  const sessionTypes = trainer?.session_offerings || [];

  const handleBookSession = async (offeringId) => {
    if (!user) {
      toast.error('Please login to book a session');
      return;
    }
    
    setBookingLoading(true);
    try {
      const response = await axios.post(`${API_URL}/payments/session/checkout`, {
        trainer_id: id,
        offering_id: offeringId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to initiate booking';
      toast.error(message);
      setBookingLoading(false);
    }
  };

  const handleSubmitReview = async (reviewData) => {
    if (!user) {
      toast.error('Please login to leave a review');
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(`${API_URL}/reviews`, {
        trainer_id: id,
        ...reviewData
      });
      toast.success('Review submitted successfully!');
      fetchTrainerData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit review';
      toast.error(message);
    } finally {
      setSubmittingReview(false);
    }
  };

  const openWhatsApp = () => {
    if (trainer?.whatsapp) {
      const phone = trainer.whatsapp.replace(/\D/g, '');
      window.open(`https://wa.me/${phone}`, '_blank');
    }
  };

  const openInstagram = () => {
    if (trainer?.instagram) {
      window.open(`https://instagram.com/${trainer.instagram}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <div className="pt-32 text-center">
          <p className="text-gray-400 text-lg">Trainer not found</p>
          <Link to="/" className="text-primary hover:underline mt-4 inline-block">
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  const hasContact = trainer.whatsapp || trainer.instagram;
  const contactLocked = trainer.contact_locked;

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="trainer-profile-page">
      <Header />
      
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={getImageUrl(trainer.photo_url)}
          alt={trainer.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/60 to-transparent" />
        
        {/* Back Button */}
        <Link
          to="/"
          className="absolute top-20 left-4 sm:left-6 p-2 rounded-full bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6 text-white" />
        </Link>
      </div>

      {/* Content */}
      <main className="relative -mt-32 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-3xl mx-auto">
          {/* Profile Header */}
          <div className="animate-fade-in-up">
            {/* Rating */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-lg font-bold text-white">{trainer.rating.toFixed(1)}</span>
              </div>
              <span className="text-gray-400 text-sm">({trainer.review_count} reviews)</span>
            </div>

            {/* Name */}
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter leading-none uppercase text-white mb-3">
              {trainer.name}
            </h1>

            {/* Location */}
            <div className="flex items-center gap-2 text-gray-400 mb-4">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">{trainer.location}</span>
            </div>

            {/* Specialties */}
            <div className="flex flex-wrap gap-2 mb-6">
              {trainer.specialties.map((specialty, index) => (
                <Badge
                  key={index}
                  className="bg-primary/20 text-primary border-primary/30 text-sm px-4 py-1.5"
                >
                  {specialty}
                </Badge>
              ))}
            </div>
          </div>

          {/* Bio */}
          <div className="bg-[#141414] rounded-2xl p-6 border border-white/5 mb-6 animate-fade-in-up animation-delay-100">
            <h2 className="font-heading text-xl font-bold uppercase text-white mb-3">About</h2>
            <p className="text-gray-300 leading-relaxed">{trainer.bio}</p>
          </div>

          {/* Book Session Button */}
          <div className="mb-6 animate-fade-in-up animation-delay-150">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setShowBookingDialog(true)}
                className="flex-1 h-16 bg-primary text-white font-bold uppercase tracking-wider text-lg rounded-xl hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all transform hover:-translate-y-0.5"
                data-testid="book-session-button"
              >
                <Calendar className="w-6 h-6 mr-3" />
                Book a Session
              </Button>
              {canMessage && (
                <Button
                  onClick={() => navigate(`/messages?to=${trainer.id}&name=${encodeURIComponent(trainer.name)}`)}
                  variant="outline"
                  className="h-16 px-8 border-primary text-primary hover:bg-primary/10 font-bold uppercase tracking-wider rounded-xl transition-all"
                  data-testid="message-trainer-button"
                >
                  <MessageSquare className="w-6 h-6 mr-3" />
                  Message
                </Button>
              )}
            </div>
            {!canMessage && user && (
              <p className="text-center text-sm text-gray-500 mt-2">
                Book a session to unlock in-app messaging with this trainer
              </p>
            )}
          </div>

          {/* Contact Buttons */}
          {hasContact && !contactLocked && (
            <div className="flex flex-col sm:flex-row gap-4 mb-8 animate-fade-in-up animation-delay-200">
              {trainer.whatsapp && (
                <Button
                  onClick={openWhatsApp}
                  className="flex-1 h-14 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold uppercase tracking-wider rounded-xl transition-all transform hover:-translate-y-0.5"
                  data-testid="whatsapp-button"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              )}
              {trainer.instagram && (
                <Button
                  onClick={openInstagram}
                  className="flex-1 h-14 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white font-bold uppercase tracking-wider rounded-xl transition-all transform hover:-translate-y-0.5"
                  data-testid="instagram-button"
                >
                  <Instagram className="w-5 h-5 mr-2" />
                  Instagram
                </Button>
              )}
            </div>
          )}
          
          {/* Contact Locked Message */}
          {contactLocked && (
            <div className="mb-8 animate-fade-in-up animation-delay-200">
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 text-center">
                <Lock className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Contact Details Locked</h3>
                <p className="text-gray-400 text-sm mb-4">
                  {trainer.contact_message || "Book a session to unlock contact details"}
                </p>
                <Button
                  onClick={() => setShowBookingDialog(true)}
                  className="bg-primary text-white hover:bg-primary/90"
                  data-testid="book-to-unlock-button"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book a Session to Unlock
                </Button>
              </div>
            </div>
          )}

          {/* Booking Dialog */}
          <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
            <DialogContent className="bg-[#141414] border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">
                  Book a Session with {trainer.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {sessionTypes.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-gray-400">This trainer hasn't set up their session pricing yet.</p>
                    <p className="text-gray-500 text-sm mt-2">Please check back later or contact them via social links.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-400 text-sm">Select your session:</p>
                    {sessionTypes.map((session) => (
                      <button
                        key={session.id}
                        onClick={() => setSelectedSession(session.id)}
                        className={`w-full p-4 rounded-xl border transition-all ${
                          selectedSession === session.id
                            ? 'border-primary bg-primary/20'
                            : 'border-white/10 bg-[#1A1A1A] hover:border-white/30'
                        }`}
                        data-testid={`session-option-${session.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-left">
                            <span className="font-semibold text-white block">{session.label}</span>
                            <span className="text-gray-500 text-sm">{session.duration} minutes</span>
                          </div>
                          <span className="text-primary font-bold text-lg">£{session.price}</span>
                        </div>
                      </button>
                    ))}
                    <Button
                      onClick={() => selectedSession && handleBookSession(selectedSession)}
                      disabled={!selectedSession || bookingLoading}
                      className="w-full h-14 bg-primary text-white font-bold uppercase tracking-wider rounded-xl mt-4"
                      data-testid="confirm-booking-button"
                    >
                      {bookingLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Proceed to Payment
                        </>
                      )}
                    </Button>
                    <p className="text-center text-gray-500 text-xs">
                      A small platform fee (10%) applies to bookings
                    </p>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Reviews Section */}
          <div className="animate-fade-in-up animation-delay-300">
            <h2 className="font-heading text-2xl font-bold uppercase text-white mb-6 flex items-center gap-2">
              <MessageCircle className="w-6 h-6" />
              Reviews ({reviews.length})
            </h2>

            {/* Review Form */}
            {user ? (
              <div className="bg-[#141414] rounded-2xl p-6 border border-white/5 mb-6">
                <h3 className="font-semibold text-white mb-4">Leave a Review</h3>
                <ReviewForm onSubmit={handleSubmitReview} isSubmitting={submittingReview} />
              </div>
            ) : (
              <div className="bg-[#141414] rounded-2xl p-6 border border-white/5 mb-6 text-center">
                <p className="text-gray-400 mb-3">Please login to leave a review</p>
                <Link to="/login">
                  <Button
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary hover:text-white"
                    data-testid="login-to-review"
                  >
                    Login to Review
                  </Button>
                </Link>
              </div>
            )}

            {/* Reviews List */}
            <div className="space-y-4">
              {reviews.length === 0 ? (
                <div className="text-center py-8 bg-[#141414] rounded-2xl border border-white/5">
                  <p className="text-gray-500">No reviews yet. Be the first to review!</p>
                </div>
              ) : (
                reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
